import React, { useEffect, useState, createElement } from 'react';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { ResultsSection } from './components/ResultsSection';
import { BenefitsSection } from './components/BenefitsSection';
import { ComparisonSection } from './components/ComparisonSection';
import { PoweredBySection } from './components/PoweredBySection';
import { IncludedSection } from './components/IncludedSection';
import { DemosSection } from './components/DemosSection';
import { PricingSection } from './components/PricingSection';
import { FaqSection } from './components/FaqSection';
import { ContactSection } from './components/ContactSection';
import { Footer } from './components/Footer';
import { SectionDivider } from './components/SectionDivider';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { TermsOfService } from './components/TermsOfService';
import { HowItWorksSection } from './components/HowItWorksSection';
import { LocalSeoContent } from './components/LocalSeoContent';
import { SchemaMarkup } from './components/SchemaMarkup';
import { BlogTeaser } from './components/BlogTeaser';
import { StickyWhatsApp } from './components/StickyWhatsApp';
import { AboutSection } from './components/AboutSection';
// Service Pages
import { ElectricianWebsiteDublin } from './pages/ElectricianWebsiteDublin';
import { RestaurantWebsiteDublin } from './pages/RestaurantWebsiteDublin';
import { BarberWebsiteDublin } from './pages/BarberWebsiteDublin';
import { TradesWebsiteDublin } from './pages/TradesWebsiteDublin';
import { HotelWebsiteIreland } from './pages/HotelWebsiteIreland';
// Blog Pages
import { BlogIndex } from './pages/BlogIndex';
import { WebsiteCostIreland } from './pages/blog/WebsiteCostIreland';
import { WebsiteDesignDublinGuide } from './pages/blog/WebsiteDesignDublinGuide';
import { ElectricianWebsiteBlog } from './pages/blog/ElectricianWebsiteBlog';
import { RestaurantWebsiteDesignBlog } from './pages/blog/RestaurantWebsiteDesignBlog';
import { SeoSmallBusinessIreland } from './pages/blog/SeoSmallBusinessIreland';
function PageWrapper({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="pt-24 min-h-screen" data-id="element-0">
      {/* SEO H1 — visually integrated but present for crawlers */}
      <h1 className="text-3xl md:text-4xl font-bold text-white text-center px-4 pt-8 pb-2" data-id="element-1">
        {title}
      </h1>
      {children}
      <SectionDivider data-id="element-2" />
      {/* Back to home CTA */}
      <div className="text-center py-12" data-id="element-3">
        <a href="/" onClick={e => {
        e.preventDefault();
        window.history.pushState({}, '', '/');
        window.dispatchEvent(new PopStateEvent('popstate'));
        window.scrollTo(0, 0);
      }} className="inline-flex items-center space-x-2 border border-[#c9a84c] text-[#c9a84c] hover:bg-[#c9a84c] hover:text-black px-6 py-3 rounded-lg font-medium transition-all duration-300" data-id="element-4">
          <span data-id="element-5">← Back to Home</span>
        </a>
      </div>
    </div>;
}
export function App() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  // Prevent text copying, cutting, and right-click context menu
  useEffect(() => {
    const preventCopy = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement;
      const tagName = target.tagName.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') return;
      e.preventDefault();
    };
    const preventContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const tagName = target.tagName.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') return;
      e.preventDefault();
    };
    document.addEventListener('copy', preventCopy);
    document.addEventListener('cut', preventCopy);
    document.addEventListener('contextmenu', preventContextMenu);
    return () => {
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('cut', preventCopy);
      document.removeEventListener('contextmenu', preventContextMenu);
    };
  }, []);
  // SEO Meta Tags
  useEffect(() => {
    const path = currentPath === '' ? '/' : currentPath;
    // Canonical tag — prevents www/non-www duplicate content
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://aiwebsitepackage.com${path}`);
    // Route-specific SEO meta (title + description)
    const seoMap: Record<string, {
      title: string;
      description: string;
    }> = {
      '/': {
        title: 'ai.web — Affordable Website Design Ireland | Web Design Dublin from €249',
        description: 'ai.web — Dublin website design for Irish small businesses. SEO optimized from €249. Mobile ready and built to convert visitors.'
      },
      '/services/electrician-website-dublin': {
        title: 'Electrician Website Design Dublin | Professional Sites From €249',
        description: 'Get a professional electrician website in Dublin. Mobile-friendly, SEO-optimised, done-for-you from €249. Rank on Google and get more emergency calls today.'
      },
      '/services/restaurant-website-dublin': {
        title: 'Restaurant Website Design Dublin | Stunning Sites From €249',
        description: 'Beautiful restaurant website design in Dublin. Showcase your menu, take reservations online, and attract diners. Done-for-you from €249 with hosting included.'
      },
      '/services/barber-website-dublin': {
        title: 'Barber Website Design Dublin | Professional Sites From €249',
        description: 'Professional barber website design in Dublin. Online bookings, gallery, pricing — all done-for-you from €249. Get found on Google and fill your appointment book.'
      },
      '/services/trades-website-dublin': {
        title: 'Tradesman Website Design Dublin | Plumber, Builder & More From €249',
        description: 'Professional website design for Dublin tradespeople. Plumbers, builders, painters — get a done-for-you site from €249 that ranks on Google and wins local jobs.'
      },
      '/services/hotel-website-ireland': {
        title: 'Hotel Website Design Ireland | Stunning Hospitality Sites From €249',
        description: 'Professional hotel website design across Ireland. Showcase rooms, amenities and drive direct bookings. Done-for-you from €249 with hosting and SEO included.'
      },
      '/blog': {
        title: 'Web Design Blog Ireland | Tips for Small Business Websites',
        description: 'Expert web design tips for Irish small businesses. Learn about website costs, SEO strategies, and how to get more customers online. Updated guides and insights.'
      },
      '/demos': {
        title: 'Website Design Demos | See Our Work For Irish Businesses',
        description: 'Browse live website demos built for Irish businesses — electricians, barbers, restaurants and more. See the quality you get with our €249 website design package.'
      },
      '/pricing': {
        title: 'Website Design Pricing Ireland | Affordable Packages From €249',
        description: 'Transparent website design pricing for Irish small businesses. One-time €249 payment includes design, hosting, SEO setup, and 1 year of support. No hidden fees.'
      },
      '/faq': {
        title: 'FAQ — Website Design Ireland | Common Questions Answered',
        description: "Frequently asked questions about our affordable website design service in Ireland. Learn about pricing, timelines, what's included, and how AI editing works."
      },
      '/contact': {
        title: 'Contact Us — Affordable Website Design Ireland',
        description: 'Get in touch for affordable website design in Ireland. WhatsApp, email or form — we reply within 1 hour. Start your €249 website package for your Dublin business today.'
      },
      '/how-it-works': {
        title: 'How It Works — Website Design Ireland in 3 Simple Steps',
        description: 'See how our website design process works. Three simple steps: tell us about your business, we build your site, you launch and grow. Done-for-you from €249.'
      },
      '/results': {
        title: 'Results — Website Design That Gets Irish Businesses Found Online',
        description: 'See the results our website designs deliver for Irish small businesses. Better Google rankings, more calls, more bookings — all from a €249 one-time investment.'
      },
      '/privacy': {
        title: 'Privacy Policy — ai.web Website Design Ireland',
        description: 'Read our privacy policy. Learn how ai.web collects, uses and protects your personal data when you use our affordable website design service in Ireland.'
      },
      '/terms': {
        title: 'Terms of Service — ai.web Website Design Ireland',
        description: 'Terms of service for ai.web affordable website design packages in Ireland. Read about our service agreement, deliverables, payment terms and support policy.'
      }
    };
    const seo = seoMap[path] || seoMap['/'];
    document.title = seo.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', seo.description);
    // Open Graph + Twitter meta tags (for social shares)
    const ensureMeta = (attr: 'property' | 'name', key: string, content: string) => {
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    ensureMeta('property', 'og:title', seo.title);
    ensureMeta('property', 'og:description', seo.description);
    ensureMeta('property', 'og:type', 'website');
    ensureMeta('property', 'og:url', `https://aiwebsitepackage.com${path}`);
    ensureMeta('property', 'og:site_name', 'ai.web');
    ensureMeta('name', 'twitter:card', 'summary_large_image');
    ensureMeta('name', 'twitter:title', seo.title);
    ensureMeta('name', 'twitter:description', seo.description);
  }, [currentPath]);
  // Microsoft Clarity analytics
  useEffect(() => {
    ;
    (function (c: any, l: any, a: string, r: string, i: string, t?: any, y?: any) {
      c[a] = c[a] || function () {
        ;
        (c[a].q = c[a].q || []).push(arguments);
      };
      t = l.createElement(r);
      t.async = 1;
      t.src = 'https://www.clarity.ms/tag/' + i;
      y = l.getElementsByTagName(r)[0];
      y.parentNode.insertBefore(t, y);
    })(window, document, 'clarity', 'script', 'vnizee75ok');
  }, []);
  // Google Analytics (gtag.js)
  useEffect(() => {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=G-3JM6MY3YFZ';
    document.head.appendChild(script);
    (window as any).dataLayer = (window as any).dataLayer || [];
    function gtag(...args: any[]) {
      ;
      (window as any).dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', 'G-3JM6MY3YFZ');
  }, []);
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, {
      threshold: 0.1
    });
    // Small delay to ensure DOM is ready after route change
    const timer = setTimeout(() => {
      const hiddenElements = document.querySelectorAll('.reveal');
      hiddenElements.forEach(el => observer.observe(el));
    }, 50);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [currentPath]);
  const renderPage = () => {
    switch (currentPath) {
      // Service Pages
      case '/services/electrician-website-dublin':
        return <ElectricianWebsiteDublin data-id="element-6" />;
      case '/services/restaurant-website-dublin':
        return <RestaurantWebsiteDublin data-id="element-7" />;
      case '/services/barber-website-dublin':
        return <BarberWebsiteDublin data-id="element-8" />;
      case '/services/trades-website-dublin':
        return <TradesWebsiteDublin data-id="element-9" />;
      case '/services/hotel-website-ireland':
        return <HotelWebsiteIreland data-id="element-10" />;
      // Blog Pages
      case '/blog':
        return <BlogIndex data-id="element-11" />;
      case '/blog/website-cost-ireland-2026':
        return <WebsiteCostIreland data-id="element-12" />;
      case '/blog/website-design-dublin-small-businesses':
        return <WebsiteDesignDublinGuide data-id="element-13" />;
      case '/blog/electrician-website-dublin':
        return <ElectricianWebsiteBlog data-id="element-14" />;
      case '/blog/restaurant-website-design-ireland':
        return <RestaurantWebsiteDesignBlog data-id="element-15" />;
      case '/blog/seo-small-businesses-ireland':
        return <SeoSmallBusinessIreland data-id="element-16" />;
      // Existing Pages
      case '/results':
        return <PageWrapper title="Results — Website Design That Gets Irish Businesses Found" data-id="element-17">
            <ResultsSection data-id="element-18" />
          </PageWrapper>;
      case '/included':
        return <PageWrapper title="What's Included In Your €249 Website Package" data-id="element-19">
            <IncludedSection data-id="element-20" />
          </PageWrapper>;
      case '/demos':
        return <PageWrapper title="Live Website Demos — Built For Irish Businesses" data-id="element-21">
            <DemosSection data-id="element-22" />
          </PageWrapper>;
      case '/pricing':
        return <PageWrapper title="Website Design Pricing Ireland — From €249" data-id="element-23">
            <PricingSection data-id="element-24" />
          </PageWrapper>;
      case '/faq':
        return <PageWrapper title="Frequently Asked Questions — Website Design Ireland" data-id="element-25">
            <FaqSection data-id="element-26" />
          </PageWrapper>;
      case '/contact':
        return <PageWrapper title="Contact Us — Get Your Website Started Today" data-id="element-27">
            <ContactSection data-id="element-28" />
          </PageWrapper>;
      case '/how-it-works':
        return <PageWrapper title="How It Works — Your Website In 3 Simple Steps" data-id="element-29">
            <HowItWorksSection data-id="element-30" />
          </PageWrapper>;
      case '/privacy':
        return <PageWrapper title="Privacy Policy" data-id="element-31">
            <PrivacyPolicy data-id="element-32" />
          </PageWrapper>;
      case '/terms':
        return <PageWrapper title="Terms of Service" data-id="element-33">
            <TermsOfService data-id="element-34" />
          </PageWrapper>;
      default:
        // Home — full landing page
        return <>
            <HeroSection data-id="element-35" />
            <LocalSeoContent data-id="element-36" />
            <ResultsSection data-id="element-37" />
            <BenefitsSection data-id="element-38" />
            <HowItWorksSection data-id="element-39" />
            <DemosSection data-id="element-40" />
            <ComparisonSection data-id="element-41" />
            <PoweredBySection data-id="element-42" />
            <IncludedSection data-id="element-43" />
            <PricingSection data-id="element-44" />
            <FaqSection data-id="element-45" />
            <BlogTeaser data-id="element-46" />
            <AboutSection data-id="element-47" />
            <ContactSection data-id="element-48" />
          </>;
    }
  };
  return <div className="min-h-screen font-sans selection:bg-[#c9a84c]/30" style={{
    backgroundColor: '#050505',
    color: '#f5f5f5'
  }} data-id="element-49">
      <SchemaMarkup data-id="element-50" />
      <div className="noise-overlay" data-id="element-51"></div>
      <Header data-id="element-52" />
      <main data-id="element-53">{renderPage()}</main>
      <Footer data-id="element-54" />
      <StickyWhatsApp data-id="element-55" />
    </div>;
}